

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import BackButton from '../../components/common/BackButton';

const STAR_NAMES = ['Beginner','Active Farmer','Growing Farmer','Influencer Farmer','Leader Farmer','Top Farmer','Elite Farmer'];
const PAGE_SIZE  = 10;

export default function FarmersPage() {
  const navigate                    = useNavigate();
  const { user: me }                = useAuth();
  const [farmers,    setFarmers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [loadingMore,setLoadingMore]= useState(false);
  const [page,       setPage]       = useState(1);
  const [hasMore,    setHasMore]    = useState(true);
  const [total,      setTotal]      = useState(0);
  const [following,  setFollowing]  = useState({});
  const [search,     setSearch]     = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall,    setShowInstall]    = useState(false);
  const loaderRef = useRef();

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstall(false);
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('App installed! 🎉');
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  useEffect(() => { loadFarmers(1, true); }, []);

  const loadFarmers = async (pageNum, reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const { data } = await api.get(`/users/farmers?page=${pageNum}&limit=${PAGE_SIZE}&search=${search}`);
      const newFarmers = data.farmers || [];
      const totalCount = data.total || newFarmers.length;
      setTotal(totalCount);
      if (reset) {
        setFarmers(newFarmers);
      } else {
        setFarmers(p => [...p, ...newFarmers]);
      }
      setHasMore(newFarmers.length === PAGE_SIZE);
      setPage(pageNum);
      // Build following map
      const map = {};
      newFarmers.forEach(f => { map[f._id] = !!f.isFollowing; });
      setFollowing(p => ({ ...p, ...map }));
    } catch {}
    finally { setLoading(false); setLoadingMore(false); }
  };

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadFarmers(page + 1);
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page]);

  const handleSearch = (val) => {
    setSearch(val);
  };

  useEffect(() => {
    const timer = setTimeout(() => loadFarmers(1, true), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleFollow = async (farmerId, e) => {
    e.stopPropagation();
    if (!me) return navigate('/login');
    const wasFollowing = following[farmerId];
    setFollowing(p => ({ ...p, [farmerId]: !wasFollowing }));
    try {
      const { data } = await api.post(`/users/${farmerId}/follow`);
      setFollowing(p => ({ ...p, [farmerId]: data.following }));
      toast.success(data.following ? '✅ Following!' : 'Unfollowed');
    } catch {
      setFollowing(p => ({ ...p, [farmerId]: wasFollowing }));
      toast.error('Error');
    }
  };

  return (
    <div className="page">
      <div className="header header--simple">
        <BackButton />
        <div>
          <div className="header__title">👨‍🌾 Farmers</div>
          <div className="header__subtitle">Connect with farmers near you</div>
        </div>
        <span style={{ marginLeft:'auto', background:'rgba(255,255,255,.2)', borderRadius:'var(--r-full)', padding:'4px 12px', color:'white', fontSize:13 }}>
          {total}
        </span>
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-3)' }}>

        {/* ── Install App Banner ── */}
        {showInstall && (
          <div style={{ background:'linear-gradient(135deg,#1A6B3C,#2E7D32)', borderRadius:16, padding:'14px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:12, boxShadow:'0 4px 16px rgba(26,107,60,.25)' }}>
            <div style={{ fontSize:32, flexShrink:0 }}>📲</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'white', marginBottom:2 }}>Install ZevaZeva App</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.8)' }}>Get faster access & work offline</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              <button onClick={handleInstall}
                style={{ background:'white', color:'#1A6B3C', border:'none', borderRadius:20, padding:'7px 16px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                Install ↓
              </button>
              <button onClick={() => setShowInstall(false)}
                style={{ background:'rgba(255,255,255,.15)', color:'white', border:'none', borderRadius:20, padding:'5px 16px', fontSize:11, cursor:'pointer' }}>
                Not now
              </button>
            </div>
          </div>
        )}

        {/* ── Search ── */}
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'white', borderRadius:12, padding:'10px 14px', marginBottom:14, border:'1px solid #E5E7EB', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <span style={{ fontSize:16, color:'#9CA3AF' }}>🔍</span>
          <input placeholder="Search farmers by name or location..."
            value={search} onChange={e => handleSearch(e.target.value)}
            style={{ flex:1, border:'none', outline:'none', fontSize:13, color:'#374151', background:'transparent' }} />
          {search && <button onClick={() => handleSearch('')} style={{ background:'none', border:'none', color:'#9CA3AF', fontSize:18, cursor:'pointer', padding:0 }}>×</button>}
        </div>

        {/* ── Farmer List ── */}
        {loading ? (
          <div className="full-center" style={{ padding:40 }}><span className="spinner spinner--lg" /></div>
        ) : farmers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">👨‍🌾</div>
            <div className="empty-state__title">{search ? 'No farmers found' : 'No farmers yet'}</div>
            <div className="empty-state__desc">{search ? 'Try a different search term' : 'Farmers will appear here once they register'}</div>
          </div>
        ) : (
          <>
            {farmers.map((farmer, idx) => {
              const isMe = me?._id === farmer._id;
              const isFollowing = following[farmer._id];
              return (
                <div key={farmer._id} className="card" style={{ marginBottom:'var(--sp-3)', cursor:'pointer', animation:`fadeIn .3s ease ${(idx % PAGE_SIZE) * 0.04}s both` }}
                  onClick={() => navigate(`/farmer/${farmer._id}`)}>
                  <div style={{ padding:'var(--sp-3) var(--sp-4)', display:'flex', alignItems:'center', gap:'var(--sp-3)' }}>
                    <div className="avatar avatar--md" style={{ flexShrink:0 }}>
                      {farmer.profilePhoto
                        ? <img src={farmer.profilePhoto} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} />
                        : (farmer.name?.[0] || 'F')}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div className="fw-semi text-base">{farmer.name}</div>
                      <div className="text-xs text-muted" style={{ marginTop:2 }}>
                         {farmer.locationAddress || 'Location not set'}
                      </div>
                      <div className="text-xs" style={{ marginTop:4, display:'flex', gap:'var(--sp-3)', flexWrap:'wrap' }}>
                        <span className="text-muted">
                          {'★'.repeat(Math.min(farmer.starLevel||0,3))}{'☆'.repeat(Math.max(0,3-(farmer.starLevel||0)))} {STAR_NAMES[Math.min(farmer.starLevel||0,6)]}
                        </span>
                        <span className="text-muted"> {farmer.cropCount||0} crops</span>
                        {farmer.readyCount > 0 && <span style={{ color:'var(--color-success)', fontWeight:600 }}>🟢 {farmer.readyCount} selling</span>}
                      </div>
                    </div>
                    {!isMe && (
                      <button onClick={e => handleFollow(farmer._id, e)}
                        className={isFollowing ? 'btn btn--outline-gray btn--sm' : 'btn btn--primary btn--sm'}
                        style={{ flexShrink:0, minWidth:80 }}>
                        {isFollowing ? '✓ Following' : '+ Follow'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* ── Load more trigger ── */}
            <div ref={loaderRef} style={{ textAlign:'center', padding:'16px 0' }}>
              {loadingMore && <span className="spinner spinner--md" />}
              {!hasMore && farmers.length > 0 && (
                <p style={{ fontSize:12, color:'#9CA3AF' }}>✓ All {total} farmers loaded</p>
              )}
            </div>
          </>
        )}

        <div style={{ height:'var(--sp-4)' }} />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
}